import { useEffect } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { LandingKey } from '../../stores/auth/types';
import { useStores } from '../../stores/useStores';

import { postUiReady } from './postUiReady';
import { mapShellDeepLink, onShellDeepLink, toDeepLinkUrl } from './shellBridge';

/**
 * A link that lands on the native lander (a guest's cold start, usually a deferred OneLink) keeps it up:
 * dropping `landing=native` would remount its flow and lose the current step.
 */
function keepNativeLanding(path: string): string {
  const target = new URL(path, window.location.origin);
  if (target.pathname === '/') {
    const current = new URLSearchParams(window.location.search);
    for (const key of ['landing', 'platform']) {
      const value = current.get(key);
      if (value) target.searchParams.set(key, value);
    }
  }

  return `${target.pathname}${target.search}${target.hash}`;
}

/**
 * Routes the shell's deep links and notification taps in-app. Called from `Layout`, the router's root, so
 * it is mounted on every route and reads the stores through context — `App` itself must not import them at
 * module scope (it would pull `RootStore` and everything behind it into whatever imports `App`).
 * A no-op outside the shell, where nothing is ever delivered.
 */
export function useShellDeepLinks(): void {
  const navigate = useNavigate();
  const { promotionStore } = useStores();

  useEffect(
    () =>
      onShellDeepLink((link) => {
        const url = toDeepLinkUrl(link);
        if (!url) return;

        const { path, promoCode } = mapShellDeepLink(url);
        const onNativeLanding = new URLSearchParams(window.location.search).get('landing') === LandingKey.NATIVE;
        if (onNativeLanding) {
          void navigate(keepNativeLanding(path), { replace: true });
        } else {
          void navigate(path);
        }
        if (promoCode) {
          void promotionStore.openFromLink(promoCode);
        }
      }),
    [navigate, promotionStore],
  );
}

/**
 * Tells the shell this page has painted (`ui.ready`) on any route but `/`, once the session is known.
 * `/` is left to `Lobby` and the native lander, which hold their own loaders past that point. Without this, a
 * cold start from a deep link to anything else (`/bingo`, `/games/:id`, `NotFound`) unmounts `Lobby` before
 * it can post, and the shell keeps its branded loader over a working page until its 10 s fallback.
 */
export function useShellUiReady(): void {
  const { authStore, userStore } = useStores();
  const { pathname } = useLocation();

  const isOwnedByLobby = pathname === '/';
  const isSessionKnown = authStore.isAuthenticationFlowDone && !userStore.loading;

  useEffect(() => {
    if (!isOwnedByLobby && isSessionKnown) {
      postUiReady();
    }
  }, [isOwnedByLobby, isSessionKnown]);
}
